/**
 * @file hooks/usePWA.ts
 * Hook React pour la gestion PWA :
 *   - Enregistrement du Service Worker
 *   - Détection de l'état offline/online
 *   - Prompt d'installation natif ("Ajouter à l'écran d'accueil")
 *   - Détection des mises à jour disponibles
 */

import { useEffect, useRef, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

export interface PWAState {
  /** Service Worker enregistré avec succès */
  swRegistered: boolean;
  /** Mise à jour disponible — afficher un bouton "Recharger" */
  updateAvailable: boolean;
  /** Application installable — afficher un bouton "Installer" */
  installable: boolean;
  /** Connexion réseau disponible */
  isOnline: boolean;
  /** L'app tourne en mode standalone (installée) */
  isStandalone: boolean;
  /** Déclencher le prompt d'installation natif */
  triggerInstall: () => Promise<void>;
  /** Forcer le rechargement pour appliquer la mise à jour */
  applyUpdate: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePWA(): PWAState {
  const [swRegistered, setSwRegistered] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const waitingWorker = useRef<ServiceWorker | null>(null);

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  // ── Enregistrement du Service Worker ────────────────────────────────────────

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        setSwRegistered(true);

        // Détecter les mises à jour disponibles
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              waitingWorker.current = newWorker;
              setUpdateAvailable(true);
            }
          });
        });

        // Vérifier périodiquement (toutes les 60 minutes)
        const interval = setInterval(() => registration.update(), 60 * 60 * 1000);
        return () => clearInterval(interval);
      })
      .catch((err) => {
        console.warn('[PWA] Service Worker non enregistré :', err);
      });
  }, []);

  // ── Prompt d'installation ────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ── État réseau ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const triggerInstall = async (): Promise<void> => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      deferredPrompt.current = null;
      setInstallable(false);
    }
  };

  const applyUpdate = (): void => {
    waitingWorker.current?.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  };

  return {
    swRegistered,
    updateAvailable,
    installable,
    isOnline,
    isStandalone,
    triggerInstall,
    applyUpdate,
  };
}
